import { __assign, __values } from "tslib";
import { getDaysInMonth, getTime, startOfYear } from '../../../libs/date';
import { clamp, isInRange } from '../../../libs/utils';
import { MAX_DEFAULT_DATE, MIN_DEFAULT_DATE } from '../constants';
import { DateComponent, DateComponents } from './DateComponents';
import { formatToParts, getResolvedOptions } from './format';
import { stepValue } from './segment';
var DateTimeFieldAdapter = /** @class */ (function () {
    function DateTimeFieldAdapter(options) {
        var formatter = options.formatter, min = options.min, max = options.max, placeholder = options.placeholder;
        var baseDate = startOfYear(new Date());
        // public
        this.formatter = formatter;
        this.resolvedOptions = getResolvedOptions(formatter);
        this.minSpecified = Boolean(min !== null && min !== void 0 ? min : false);
        this.maxSpecified = Boolean(max !== null && max !== void 0 ? max : false);
        this.minTime = getTime(min !== null && min !== void 0 ? min : MIN_DEFAULT_DATE);
        this.maxTime = getTime(max !== null && max !== void 0 ? max : MAX_DEFAULT_DATE);
        this.min = DateComponents.from(this.minTime);
        this.max = DateComponents.from(this.maxTime);
        this.placeholder = getTime(placeholder !== null && placeholder !== void 0 ? placeholder : baseDate);
        // private
        this.numberFormatter = new Intl.NumberFormat(this.resolvedOptions.locale, {
            useGrouping: false,
        });
        this.placeholderParts = formatToParts(this.formatter, this.placeholder);
        this.requiredSegments = this.getSegmentsMaskFromFormatParts(this.placeholderParts);
        this.baseDate = baseDate;
    }
    DateTimeFieldAdapter.prototype.getSegments = function (dateComponents) {
        var _this = this;
        return this.formatToParts(dateComponents).map(function (part) {
            if (!DateComponents.isDateComponentType(part.type)) {
                return {
                    type: part.type,
                    text: part.value,
                    isEditable: false,
                };
            }
            var value = dateComponents.get(part.type);
            var _a = _this.getLimits(dateComponents, part.type), min = _a.min, max = _a.max;
            var isPlaceholder = value === null;
            var isValid = value === null || isInRange(value, min, max);
            var isDisabled = !isPlaceholder && isValid && min === max;
            return {
                type: part.type,
                text: part.value,
                isEditable: true,
                value: value,
                min: min,
                max: max,
                isDisabled: isDisabled,
                isValid: isValid,
                isPlaceholder: isPlaceholder,
            };
        });
    };
    DateTimeFieldAdapter.prototype.getLimits = function (dateComponents, type) {
        var limits = { min: 0, max: 0 };
        switch (type) {
            case 'year':
                limits.min = this.min.get('year');
                limits.max = this.max.get('year');
                break;
            case 'month':
                limits.min = 1;
                limits.max = 12;
                if (DateComponents.isSame('year', dateComponents, this.min)) {
                    limits.min = this.min.get('month');
                }
                if (DateComponents.isSame('year', dateComponents, this.max)) {
                    limits.max = this.max.get('month');
                }
                break;
            case 'day':
                limits.min = 1;
                limits.max = 31;
                if (dateComponents.has(DateComponent.year | DateComponent.month)) {
                    var date = new Date(dateComponents.get('year'), dateComponents.get('month') - 1);
                    limits.max = getDaysInMonth(date);
                }
                if (DateComponents.isSame('month', dateComponents, this.min)) {
                    limits.min = this.min.get('day');
                }
                if (DateComponents.isSame('month', dateComponents, this.max)) {
                    limits.max = this.max.get('day');
                }
                break;
            case 'hour':
                limits.min = 0;
                limits.max = 23;
                if (DateComponents.isSame('day', dateComponents, this.min)) {
                    limits.min = this.min.get('hour');
                }
                if (DateComponents.isSame('day', dateComponents, this.max)) {
                    limits.max = this.max.get('hour');
                }
                break;
            case 'dayPeriod':
                limits.min = 1;
                limits.max = 2;
                if (DateComponents.isSame('day', dateComponents, this.min)) {
                    limits.min = this.min.get('dayPeriod');
                }
                if (DateComponents.isSame('day', dateComponents, this.max)) {
                    limits.max = this.max.get('dayPeriod');
                }
                break;
            case 'minute':
                limits.min = 0;
                limits.max = 59;
                if (DateComponents.isSame('hour', dateComponents, this.min)) {
                    limits.min = this.min.get('minute');
                }
                if (DateComponents.isSame('hour', dateComponents, this.max)) {
                    limits.max = this.max.get('minute');
                }
                break;
            case 'second':
                limits.min = 0;
                limits.max = 59;
                if (DateComponents.isSame('minute', dateComponents, this.min)) {
                    limits.min = this.min.get('second');
                }
                if (DateComponents.isSame('minute', dateComponents, this.max)) {
                    limits.max = this.max.get('second');
                }
                break;
        }
        return limits;
    };
    DateTimeFieldAdapter.prototype.moveByStep = function (dateComponents, type, step) {
        var segmentValue = dateComponents.get(type);
        var _a = this.getLimits(dateComponents, type), min = _a.min, max = _a.max;
        if (segmentValue === null || segmentValue < min || segmentValue > max) {
            return this.getInitialValueForStep(type, step, min, max);
        }
        return stepValue(clamp(segmentValue, min, max), step, min, max, type === 'hour' || type === 'minute' || type === 'second');
    };
    DateTimeFieldAdapter.prototype.setSegmentValue = function (dateComponents, type, segmentValue) {
        if (segmentValue !== null) {
            var limits = this.getLimits(dateComponents, type);
            segmentValue = clamp(segmentValue, limits.min, limits.max);
        }
        return dateComponents.with(type, segmentValue);
    };
    DateTimeFieldAdapter.prototype.toDate = function (dateComponents) {
        var date = dateComponents.toDate(this.baseDate);
        var day = date.getDate();
        if (dateComponents.has(this.requiredSegments) &&
            day === dateComponents.get('day', day) &&
            isInRange(date.getTime(), this.minTime, this.maxTime)) {
            return date;
        }
        return null;
    };
    DateTimeFieldAdapter.prototype.normalizeDateComponents = function (dateComponents) {
        var e_1, _a;
        var types = [
            'year',
            'minute',
            'day',
            'hour',
            'month',
            'second',
        ];
        var nextDateComponents = dateComponents;
        try {
            for (var types_1 = __values(types), types_1_1 = types_1.next(); !types_1_1.done; types_1_1 = types_1.next()) {
                var type = types_1_1.value;
                var value = dateComponents.get(type);
                if (value === null && DateComponents.isSame(type, this.min, this.max)) {
                    nextDateComponents = nextDateComponents.with(type, this.min.get(type));
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (types_1_1 && !types_1_1.done && (_a = types_1.return)) _a.call(types_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return nextDateComponents !== dateComponents ? nextDateComponents : dateComponents;
    };
    DateTimeFieldAdapter.prototype.formatValue = function (value, format) {
        var formatted = this.numberFormatter.format(value);
        if (format === 'numeric') {
            return formatted;
        }
        return formatted.padStart(2, this.numberFormatter.format(0));
    };
    DateTimeFieldAdapter.prototype.getInitialValueForStep = function (type, step, min, max) {
        if (type === 'year') {
            // if the user has specified a minimum value, then we use it
            if (step > 0 && this.minSpecified) {
                return min;
            }
            // if the user has specified a maximum value, then we use it
            if (step < 0 && this.maxSpecified) {
                return max;
            }
            return clamp(this.baseDate.getFullYear(), min, max);
        }
        return step > 0 ? this.min.get(type, min) : this.max.get(type, max);
    };
    DateTimeFieldAdapter.prototype.getSegmentsMaskFromFormatParts = function (parts) {
        return parts.reduce(function (state, part) {
            if (DateComponents.isDateComponentType(part.type)) {
                return state | DateComponent[part.type];
            }
            return state;
        }, 0);
    };
    DateTimeFieldAdapter.prototype.formatToParts = function (dateComponents) {
        var _this = this;
        var parts = formatToParts(this.formatter, dateComponents.toDate(new Date(this.placeholder)));
        return parts.map(function (part, index) {
            if (DateComponents.isDateComponentType(part.type)) {
                var value = dateComponents.get(part.type);
                if (value === null) {
                    return _this.placeholderParts[index];
                }
                if (part.type === 'day') {
                    var format = _this.resolvedOptions.day;
                    return __assign(__assign({}, part), { value: _this.formatValue(value, format) });
                }
            }
            return part;
        });
    };
    return DateTimeFieldAdapter;
}());
export { DateTimeFieldAdapter };
